import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import StatisticsSelectionHeader from '../components/statistics/StatisticsSelectionHeader';
import StatisticsSummary from '../components/statistics/StatisticsSummary';
import StatisticsTabs from '../components/statistics/StatisticsTabs';
import StatisticsUsersTab from '../components/statistics/StatisticsUsersTab';
import StatisticsClientsTab from '../components/statistics/StatisticsClientsTab';
import StatisticsEvolutionTab from '../components/statistics/StatisticsEvolutionTab';
import StatisticsAIUsageTab from '../components/statistics/StatisticsAIUsageTab';
import useAnalytics from '../hooks/useAnalytics';
import useTabParam from '../hooks/useTabParam.js';

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0 (Sun) .. 6 (Sat)
  const diffToMonday = (day === 0 ? -6 : 1) - day; // Monday as start
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

function StatisticsViewer({ fetchWithAuth, currentUser }) {
  const initial = useMemo(() => getWeekRange(), []);
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statsData, setStatsData] = useState({ current: [], previous: [], metadata: {} });
  const [weeklyData, setWeeklyData] = useState([]);
  const [activeTab, setActiveTab] = useTabParam(['general', 'ai-usage', 'evolution', 'clients', 'users'], 'general');
  const [clientFilter, setClientFilter] = useState(null);
  const [toolFilter, setToolFilter] = useState(null);
  const [userFilter, setUserFilter] = useState(null);
  const [includeInternal, setIncludeInternal] = useState(false);

  useAnalytics({ currentUser, module: 'STATISTICS' });

  const onDatesChange = ({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);
  };

  const isInternalClient = (clientName) => {
    if (!clientName) return false;
    const upper = clientName.toUpperCase();
    return upper.includes('GLOBE') || upper.includes('PRIMER IMPACTO');
  };

  const filteredStatsData = useMemo(() => {
    if (includeInternal) {
      return statsData;
    }
    const filterFn = (row) => !isInternalClient(row.client);
    return {
      current: (statsData.current || []).filter(filterFn),
      previous: (statsData.previous || []).filter(filterFn),
      metadata: statsData.metadata
    };
  }, [statsData, includeInternal]);

  useEffect(() => {
    const load = async () => {
      setError('');
      setLoading(true);
      try {
        const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate) });

        if (clientFilter?.value) params.set('client', clientFilter.value);
        if (toolFilter?.value) params.set('tool', toolFilter.value);
        if (userFilter?.value) params.set('user', userFilter.value);

        const url = `${API_BASE_URL}/api/dwh/bi/tools/stats?${params.toString()}`;
        const resp = await fetchWithAuth(url);

        if (!resp.ok) {
          setError('Failed to load statistics');
          setStatsData({ current: [], previous: [], metadata: {} });
          return;
        }

        const json = await resp.json();

        setStatsData({ current: json.data.current || [], previous: json.data.previous || [], metadata: json.metadata || {} });
        // Compute weekly in front if needed later; currently using stacked summary only
        setWeeklyData([]);
      } catch (e) {
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [startDate, endDate, clientFilter, toolFilter, userFilter, fetchWithAuth]);

  const summary = useMemo(() => {
    const sum = (arr, key) => arr.reduce((acc, x) => acc + (Number(x[key]) || 0), 0);
    const uniq = (arr, key) => new Set(arr.map(x => x[key])).size;
    const totalVisits = sum(filteredStatsData.current, 'totalVisits');
    const totalEvents = sum(filteredStatsData.current, 'totalEvents');
    const totalVisitsPrev = sum(filteredStatsData.previous, 'totalVisits');
    const totalEventsPrev = sum(filteredStatsData.previous, 'totalEvents');
    const clients = uniq(filteredStatsData.current, 'client');
    const tools = uniq(filteredStatsData.current, 'tool');
    const clientsPrev = uniq(filteredStatsData.previous, 'client');
    const toolsPrev = uniq(filteredStatsData.previous, 'tool');
    const avgEventsPerVisit = totalVisits > 0 ? (totalEvents / totalVisits) : 0;
    const avgEventsPerVisitPrev = totalVisitsPrev > 0 ? (totalEventsPrev / totalVisitsPrev) : 0;

    return {
      totalVisits, totalEvents, clients, tools, avgEventsPerVisit,
      previous: { totalVisits: totalVisitsPrev, totalEvents: totalEventsPrev, clients: clientsPrev, tools: toolsPrev, avgEventsPerVisit: avgEventsPerVisitPrev }
    };
  }, [filteredStatsData]);

  // Aggregations: consolidated by client and by tool, with previous period comparisons (including users)
  const clientAggregates = useMemo(() => {
    const sumMap = new Map();
    const prevMap = new Map();

    (filteredStatsData.current || []).forEach(row => {
      const key = row.client || '—';
      if (!sumMap.has(key)) sumMap.set(key, { client: key, visits: 0, events: 0, users: 0 });
      const acc = sumMap.get(key);
      acc.visits += Number(row.totalVisits) || 0;
      acc.events += Number(row.totalEvents) || 0;
      acc.users += Number(row.users) || 0; // note: sum across tools (approximation)
    });

    (filteredStatsData.previous || []).forEach(row => {
      const key = row.client || '—';
      if (!prevMap.has(key)) prevMap.set(key, { visits: 0, events: 0, users: 0 });
      const acc = prevMap.get(key);
      acc.visits += Number(row.totalVisits) || 0;
      acc.events += Number(row.totalEvents) || 0;
      acc.users += Number(row.users) || 0;
    });

    const toPct = (delta, prev, curr) => {
      if (prev > 0) return (delta / prev) * 100;
      return curr > 0 ? 100 : 0;
    };

    return Array.from(sumMap.values()).map(item => {
      const prev = prevMap.get(item.client) || { visits: 0, events: 0, users: 0 };
      const deltaVisits = item.visits - prev.visits;
      const deltaEvents = item.events - prev.events;
      const deltaUsers = item.users - prev.users;
      const deltaVisitsPct = toPct(deltaVisits, prev.visits, item.visits);
      const deltaEventsPct = toPct(deltaEvents, prev.events, item.events);
      const deltaUsersPct = toPct(deltaUsers, prev.users, item.users);

      return {
        client: item.client,
        visits: item.visits,
        visitsPrev: prev.visits,
        deltaVisits,
        deltaVisitsPct,
        events: item.events,
        eventsPrev: prev.events,
        deltaEvents,
        deltaEventsPct,
        users: item.users,
        usersPrev: prev.users,
        deltaUsers,
        deltaUsersPct
      };
    }).filter(item => item.client !== '(none)').sort((a, b) => b.visits - a.visits);
  }, [filteredStatsData]);

  const toolAggregates = useMemo(() => {
    const sumMap = new Map();
    const prevMap = new Map();

    (filteredStatsData.current || []).forEach(row => {
      const key = row.tool || '—';
      if (!sumMap.has(key)) sumMap.set(key, { tool: key, visits: 0, events: 0, users: 0 });
      const acc = sumMap.get(key);
      acc.visits += Number(row.totalVisits) || 0;
      acc.events += Number(row.totalEvents) || 0;
      acc.users += Number(row.users) || 0; // approximation sum across clients
    });

    (filteredStatsData.previous || []).forEach(row => {
      const key = row.tool || '—';
      if (!prevMap.has(key)) prevMap.set(key, { visits: 0, events: 0, users: 0 });
      const acc = prevMap.get(key);
      acc.visits += Number(row.totalVisits) || 0;
      acc.events += Number(row.totalEvents) || 0;
      acc.users += Number(row.users) || 0;
    });

    const toPct = (delta, prev, curr) => {
      if (prev > 0) return (delta / prev) * 100;
      return curr > 0 ? 100 : 0;
    };

    return Array.from(sumMap.values()).map(item => {
      const prev = prevMap.get(item.tool) || { visits: 0, events: 0, users: 0 };
      const deltaVisits = item.visits - prev.visits;
      const deltaEvents = item.events - prev.events;
      const deltaUsers = item.users - prev.users;
      const deltaVisitsPct = toPct(deltaVisits, prev.visits, item.visits);
      const deltaEventsPct = toPct(deltaEvents, prev.events, item.events);
      const deltaUsersPct = toPct(deltaUsers, prev.users, item.users);

      return {
        tool: item.tool,
        visits: item.visits,
        visitsPrev: prev.visits,
        deltaVisits,
        deltaVisitsPct,
        events: item.events,
        eventsPrev: prev.events,
        deltaEvents,
        deltaEventsPct,
        users: item.users,
        usersPrev: prev.users,
        deltaUsers,
        deltaUsersPct
      };
    }).sort((a, b) => b.visits - a.visits);
  }, [filteredStatsData]);

  return (
    <div className="container mx-auto p-4">
      {/* Header + filters */}
      <StatisticsSelectionHeader
        startDate={startDate}
        endDate={endDate}
        onDatesChange={onDatesChange}
        tools={[...new Set(filteredStatsData.current.map(r => r.tool))].filter(Boolean).map(t => ({ value: t, label: t }))}
        selectedTool={toolFilter}
        onToolChange={setToolFilter}
        clients={[...new Set(filteredStatsData.current.map(r => r.client))].filter(Boolean).map(c => ({ value: c, label: c }))}
        selectedClient={clientFilter}
        onClientChange={setClientFilter}
        users={(weeklyData.usersOptions || [])}
        selectedUser={userFilter}
        onUserChange={setUserFilter}
        includeInternal={includeInternal}
        setIncludeInternal={setIncludeInternal}
      />

      {/* Tabs */}
      <StatisticsTabs
        tabs={[
          {
            id: 'general',
            label: 'Général',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path></svg>
            ),
            content: (
            <>
              {/* Loading / Error */}
              {loading && (
                <div className="flex justify-center my-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              )}

              {error && !loading && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-md">
                  {error}
                </div>
              )}

              {!loading && !error && (
                <StatisticsSummary
                  summary={summary}
                  clientAggregates={clientAggregates}
                  toolAggregates={toolAggregates}
                  startDate={startDate}
                  endDate={endDate}
                  clientFilter={clientFilter}
                  toolFilter={toolFilter}
                />
              )}

            </>
            )
          },
          {
            id: 'ai-usage',
            label: 'AI usage',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 3a.75.75 0 01.75.75V6h3V3.75a.75.75 0 011.5 0V6h.25A2.75 2.75 0 0118 8.75V10h1.25a.75.75 0 010 1.5H18v2h1.25a.75.75 0 010 1.5H18v1.25A2.75 2.75 0 0115.25 19H15v1.25a.75.75 0 01-1.5 0V19h-3v1.25a.75.75 0 01-1.5 0V19h-.25A2.75 2.75 0 016 16.25V15H4.75a.75.75 0 010-1.5H6v-2H4.75a.75.75 0 010-1.5H6V8.75A2.75 2.75 0 018.75 6H9V3.75A.75.75 0 019.75 3zM8.75 7.5A1.25 1.25 0 007.5 8.75v7.5c0 .69.56 1.25 1.25 1.25h6.5c.69 0 1.25-.56 1.25-1.25v-7.5c0-.69-.56-1.25-1.25-1.25h-6.5z" />
              </svg>
            ),
            content: (
              <StatisticsAIUsageTab
                startDate={formatYMD(startDate)}
                endDate={formatYMD(endDate)}
                fetchWithAuth={fetchWithAuth}
                apiBaseUrl={API_BASE_URL}
              />
            )
          },
          {
            id: 'evolution',
            label: 'Evolution usages',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            ),
            content: (
              <StatisticsEvolutionTab
                startDate={startDate}
                endDate={endDate}
                clientFilter={clientFilter}
                toolFilter={toolFilter}
                fetchWithAuth={fetchWithAuth}
                apiBaseUrl={API_BASE_URL}
                includeInternal={includeInternal}
              />
            )
          },
          {
            id: 'clients',
            label: 'Clients',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            ),
            content: (
              <StatisticsClientsTab
                startDate={startDate}
                endDate={endDate}
                clientFilter={clientFilter}
                toolFilter={toolFilter}
                fetchWithAuth={fetchWithAuth}
                apiBaseUrl={API_BASE_URL}
                includeInternal={includeInternal}
              />
            )
          },
          {
            id: 'users',
            label: 'Utilisateurs',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197a4 4 0 010-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ),
            content: (
              <StatisticsUsersTab
                startDate={startDate}
                endDate={endDate}
                clientFilter={clientFilter}
                fetchWithAuth={fetchWithAuth}
                apiBaseUrl={API_BASE_URL}
                toolFilter={toolFilter}
                includeInternal={includeInternal}
              />
            )
          },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

    </div>
  );
}

export default StatisticsViewer;


